import React, { useState, useRef, useEffect, useCallback } from 'react';
import { InputBase, Box, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';

interface WysiwygInputProps {
  value: string;
  onChange: (value: string) => void;
  onSave: (value: string) => void;
  onCancel?: () => void;
  placeholder?: string;
  isEditing: boolean;
  onEditingChange: (editing: boolean) => void;
  autoFocus?: boolean;
  multiline?: boolean;
  fontSize?: string;
  fontWeight?: string | number;
  showHoverIndication?: boolean;
  hoverText?: string;
  maxWidth?: string;
  disabled?: boolean;
  variant?: 'title' | 'content';
  allowEnterToSave?: boolean;
  isTemplate?: boolean;
}

// Styled components
const ViewContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'showHover' && prop !== 'variant'
})<{ showHover?: boolean; variant?: string }>(({ theme, showHover, variant }) => ({
  display: 'inline-block',
  width: 'auto',
  maxWidth: '100%',
  borderRadius: '4px',
  padding: variant === 'title' ? '6px 8px' : '8px 12px',
  minHeight: variant === 'title' ? '32px' : '40px',
  transition: 'all 0.2s ease',
  cursor: 'text',
  position: 'relative',
  
  ...(showHover && {
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
      boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
      border: '1px solid rgba(0, 0, 0, 0.08)'
    }
  })
}));

const EditContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'variant'
})<{ variant?: string }>(({ theme, variant }) => ({
  display: 'inline-block',
  width: '100%',
  maxWidth: '100%',
  borderRadius: '4px',
  padding: variant === 'title' ? '6px 8px' : '8px 12px',
  backgroundColor: '#ffffff',
  border: '1px solid #e0e0e0',
  boxShadow: '0 0 0 2px rgba(9, 133, 227, 0.2)',
  transition: 'all 0.2s ease'
}));

const StyledText = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isEmpty'
})<{ isEmpty?: boolean }>(({ theme, isEmpty }) => ({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  color: isEmpty ? theme.palette.text.secondary : 'inherit',
  fontStyle: isEmpty ? 'italic' : 'normal'
}));

const StyledInput = styled(InputBase)(({ theme }) => ({
  width: '100%',
  '& .MuiInputBase-input': {
    padding: 0,
    width: '100%'
  },
  '& .MuiInputBase-inputMultiline': {
    padding: 0,
    resize: 'vertical',
    minHeight: '20px'
  }
}));

const WysiwygInput: React.FC<WysiwygInputProps> = ({
  value,
  onChange,
  onSave,
  onCancel,
  placeholder = '',
  isEditing,
  onEditingChange,
  autoFocus = false,
  multiline = false,
  fontSize = '16px',
  fontWeight = 'normal',
  showHoverIndication = false,
  hoverText = 'Click to edit',
  maxWidth = '100%',
  disabled = false,
  variant = 'content',
  allowEnterToSave = false,
  isTemplate = false
}) => {
  // Debug log when component renders
  console.log('🎨 WysiwygInput rendered:', { 
    placeholder, 
    isEditing, 
    multiline, 
    allowEnterToSave, 
    variant,
    isTemplate,
    value: value?.slice(0, 50) + (value?.length > 50 ? '...' : '')
  });
  const [localValue, setLocalValue] = useState(value);
  const [isTruncated, setIsTruncated] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveRequestRef = useRef<Promise<void> | null>(null);

  // Update local value when prop value changes (but only when not editing)
  useEffect(() => {
    if (!isEditing) {
      setLocalValue(value);
    }
  }, [value, isEditing]);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
        if (variant === 'title' && !multiline) {
          inputRef.current?.select();
        }
      }, 50);
    }
  }, [isEditing, variant, multiline]);

  // Check if text is truncated
  useEffect(() => {
    if (textRef.current && !isEditing && variant === 'title') {
      const element = textRef.current;
      const isTextTruncated = element.scrollWidth > element.clientWidth;
      setIsTruncated(isTextTruncated);
    }
  }, [localValue, isEditing, variant]);

  // Auto-save functionality - defined without handleSave dependency to avoid circular reference
  const scheduleAutoSave = useCallback(() => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    
    autoSaveTimerRef.current = setTimeout(async () => {
      if (isEditing && !isSaving) {
        // Call handleSave directly here to avoid circular dependency
        const trimmedValue = localValue.trim();
        
        // Don't save if value hasn't changed or is empty
        if (trimmedValue === value) {
          return;
        }

        setIsSaving(true);
        
        try {
          const savePromise = Promise.resolve(onSave(trimmedValue));
          saveRequestRef.current = savePromise;
          await savePromise;
          onEditingChange(false);
        } catch (error) {
          console.error('Auto-save failed:', error);
        } finally {
          setIsSaving(false);
          saveRequestRef.current = null;
        }
      }
    }, 3000); // 3 seconds like Telemore
  }, [isEditing, isSaving, localValue, value, onSave, onEditingChange]);

  // Clear auto-save timer
  const clearAutoSave = useCallback(() => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
  }, []);

  // Cleanup auto-save timer on unmount and clear save request
  useEffect(() => {
    return () => {
      clearAutoSave();
      saveRequestRef.current = null;
    };
  }, [clearAutoSave]);

  // Reset saving state when editing stops
  useEffect(() => {
    if (!isEditing) {
      setIsSaving(false);
      saveRequestRef.current = null;
    }
  }, [isEditing]);

  const handleViewClick = () => {
    if (!disabled && !isEditing) {
      onEditingChange(true);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    onChange(newValue);
    scheduleAutoSave();
  };

  const handleSave = useCallback(async () => {
    console.log('💾 handleSave called:', {
      isSaving,
      localValue,
      value,
      trimmedValue: localValue.trim(),
      isTemplate
    });
    
    // Prevent concurrent saves
    if (isSaving || saveRequestRef.current) {
      console.log('🚫 handleSave blocked - concurrent save prevented');
      return;
    }

    const trimmedValue = localValue.trim();
    
    // Don't save if value is empty
    if (!trimmedValue) {
      console.log('🚫 handleSave blocked - empty value');
      clearAutoSave();
      onEditingChange(false);
      return;
    }
    
    // Don't save if value hasn't changed (but allow save if original was empty or if this is a template)
    // Template items need to always save since they start with empty value
    if (trimmedValue === value && value !== '' && !isTemplate) {
      console.log('🚫 handleSave blocked - value unchanged');
      clearAutoSave();
      onEditingChange(false);
      return;
    }
    
    console.log('✅ handleSave proceeding - calling onSave prop');
    setIsSaving(true);
    clearAutoSave();

    try {
      // Create a promise to track this save operation and await it
      const savePromise = Promise.resolve(onSave(trimmedValue));
      saveRequestRef.current = savePromise;
      await savePromise;
      
      onEditingChange(false);
    } catch (error) {
      console.error('Save failed:', error);
      // Don't exit editing mode on error so user can retry
    } finally {
      setIsSaving(false);
      saveRequestRef.current = null;
    }
  }, [localValue, value, onSave, onEditingChange, clearAutoSave, isSaving]);

  const handleCancel = useCallback(() => {
    setLocalValue(value);
    onChange(value);
    clearAutoSave();
    setIsSaving(false);
    saveRequestRef.current = null;
    if (onCancel) {
      onCancel();
    } else {
      onEditingChange(false);
    }
  }, [value, onChange, onCancel, onEditingChange, clearAutoSave]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation();
    
    console.log('🎹 WysiwygInput keydown:', {
      key: e.key,
      multiline,
      allowEnterToSave,
      localValue,
      placeholder
    });
    
    if (e.key === 'Escape') {
      console.log('🔙 Escape pressed - calling handleCancel');
      handleCancel();
    } else if (e.key === 'Enter') {
      // For single-line inputs, always save on Enter
      // For multiline inputs, save on Ctrl+Enter or when allowEnterToSave is true
      const shouldSave = !multiline || (multiline && e.ctrlKey) || (multiline && allowEnterToSave);
      
      console.log('⏎ Enter pressed:', {
        multiline,
        ctrlKey: e.ctrlKey,
        allowEnterToSave,
        shouldSave
      });
      
      if (shouldSave) {
        console.log('💾 Calling handleSave');
        e.preventDefault();
        handleSave();
      }
    }
  };

  const handleBlur = useCallback(() => {
    // Small timeout to prevent conflicts with other handlers
    setTimeout(() => {
      if (isEditing && !isSaving) {
        handleSave();
      }
    }, 100);
  }, [isEditing, handleSave, isSaving]);

  // Handle clicks outside the input
  useEffect(() => {
    if (isEditing) {
      const handleClickOutside = (event: MouseEvent) => {
        if (inputRef.current && !inputRef.current.contains(event.target as Node) && !isSaving) {
          handleSave();
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isEditing, handleSave, isSaving]);

  if (isEditing) {
    return (
      <EditContainer variant={variant} sx={{ maxWidth }}>
        <StyledInput
          inputRef={inputRef}
          value={localValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder={placeholder}
          multiline={multiline}
          autoFocus={autoFocus}
          onClick={(e) => e.stopPropagation()}
          sx={{
            fontSize,
            fontWeight,
            '& .MuiInputBase-input': {
              fontSize,
              fontWeight
            }
          }}
        />
      </EditContainer>
    );
  }

  const displayValue = localValue || placeholder;
  const isEmpty = !localValue;

  const content = (
    <ViewContainer 
      showHover={showHoverIndication && !disabled} 
      variant={variant}
      onClick={handleViewClick}
      sx={{ maxWidth }}
    >
      <StyledText 
        ref={textRef}
        isEmpty={isEmpty}
        sx={{ 
          fontSize, 
          fontWeight,
          whiteSpace: multiline ? 'pre-wrap' : 'nowrap'
        }}
      >
        {displayValue}
      </StyledText>
    </ViewContainer>
  );

  // Show tooltip for truncated text or hover indication
  const shouldShowTooltip = (isTruncated && localValue) || (showHoverIndication && !disabled);
  const tooltipTitle = isTruncated && localValue ? localValue : hoverText;

  if (shouldShowTooltip) {
    return (
      <Tooltip 
        title={tooltipTitle}
        placement="top"
        arrow
        disableHoverListener={!shouldShowTooltip}
      >
        {content}
      </Tooltip>
    );
  }

  return content;
};

export default WysiwygInput;